import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
    X, Play, Square, Calendar, User, Flag, FolderKanban, Clock,
    MessageSquare, Paperclip, Edit3, Save, Trash2, Plus, Send,
    RefreshCw, Users, CheckCircle2, Upload, FileText, Image, File,
    ChevronDown, ChevronUp, AlertCircle, ListChecks, Bell
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import TaskChecklist from './TaskChecklist';
import TaskReminderPicker, { formatReminder } from './TaskReminderPicker';

const priorityConfig = {
    1: { label: 'Hoch', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10' },
    2: { label: 'Mittel', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/10' },
    3: { label: 'Niedrig', color: 'bg-gray-400', textColor: 'text-gray-400', bgLight: 'bg-gray-400/10' },
    0: { label: 'Nicht dringend', color: 'bg-green-500', textColor: 'text-green-500', bgLight: 'bg-green-500/10' }
};

const TaskDetailPanelFull = ({
    task,
    users,
    projects,
    customers,
    statusConfig,
    onClose,
    onOpenFullModal,
    onUpdate,
    onDelete,
    onAddComment,
    onDeleteComment,
    onFileUpload,
    onFileDelete,
    isTracking,
    activeTaskId,
    elapsedSeconds,
    formattedTime,
    onStartTracking,
    onStopTracking,
    onCancelTracking,
    activityTypes,
    api
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [newComment, setNewComment] = useState('');
    const [showTimeForm, setShowTimeForm] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        details: true,
        time: true,
        checklist: true,
        comments: true,
        files: true
    });
    const [timeStopForm, setTimeStopForm] = useState({
        customer_id: '',
        activity_type: '',
        description: ''
    });
    const [uploading, setUploading] = useState(false);
    const [projectsEnabled, setProjectsEnabled] = useState(true);
    const fileInputRef = useRef(null);

    const isTrackingThisTask = isTracking && activeTaskId === task?.id;

    // Load projects_enabled setting
    useEffect(() => {
        if (api) {
            api.get('/system/settings').then(res => {
                if (res.data?.projects_enabled !== undefined) {
                    setProjectsEnabled(res.data.projects_enabled);
                }
            }).catch(() => {});
        }
    }, [api]);

    // Get account managers (chef/admin roles)
    const accountManagers = users?.filter(u => ['chef', 'admin', 'account_manager'].includes(u.role)) || [];

    useEffect(() => {
        if (task) {
            setEditData({
                title: task.title || '',
                description: task.description || '',
                status: task.status || 'open',
                priority: task.priority ?? 2,
                deadline: task.deadline?.split('T')[0] || '',
                deadline_time: task.deadline?.includes('T') ? task.deadline.split('T')[1]?.substring(0, 5) : '',
                assigned_to: task.assigned_users?.map(u => u.id) || [],
                project_id: task.project_id || '',
                customer_id: task.customer_id || '',
                account_manager_id: task.account_manager_id || '',
                estimated_hours: task.estimated_hours || '',
                is_recurring: task.is_recurring || false,
                recurrence_pattern: task.recurrence_pattern || 'none',
                reminders: task.reminders || []
            });
            // Pre-fill time stop form with task data
            setTimeStopForm({
                customer_id: task.customer_id || '',
                activity_type: '',
                description: `Arbeit an: ${task.title}`
            });
        }
    }, [task]);

    // Handle X click - just close the panel
    const handleCloseClick = () => {
        onClose();
    };
    
    // Handle opening full modal
    const handleOpenFullModalClick = () => {
        if (onOpenFullModal) {
            onOpenFullModal(task);
        }
        onClose();
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleSave = async () => {
        try {
            const updateData = {
                ...editData,
                deadline: editData.deadline 
                    ? (editData.deadline_time 
                        ? `${editData.deadline}T${editData.deadline_time}:00` 
                        : editData.deadline)
                    : null
            };
            delete updateData.deadline_time;
            await onUpdate(task.id, updateData);
            setIsEditing(false);
            toast.success('Task gespeichert');
        } catch (e) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            await onAddComment(task.id, newComment);
            setNewComment('');
        } catch (e) {
            toast.error('Fehler beim Hinzufügen des Kommentars');
        }
    };

    const handleFileUpload = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        try {
            await onFileUpload(task.id, files[0]);
            toast.success('Datei hochgeladen');
        } catch (e) {
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleStopTime = async () => {
        try {
            await onStopTracking(
                timeStopForm.customer_id || null,
                null,
                timeStopForm.description,
                false
            );
            setShowTimeForm(false);
            toast.success('Zeit gespeichert');
        } catch (e) {
            console.error('Time save error:', e);
            const errorMsg = e?.response?.data?.detail || e?.message || 'Unbekannter Fehler';
            toast.error(`Fehler: ${errorMsg}`);
        }
    };

    const handleCancelTime = () => {
        if (onCancelTracking && typeof onCancelTracking === 'function') {
            onCancelTracking();
        }
        setShowTimeForm(false);
        toast.info('Zeit verworfen');
    };

    const formatTimeDisplay = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getFileIcon = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4" />;
        if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return <FileText className="w-4 h-4" />;
        return <File className="w-4 h-4" />;
    };

    if (!task) return null;

    const project = projects?.find(p => p.id === task.project_id);
    const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !statusConfig[task.status]?.is_done;

    return (
        <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '450px', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="border-l border-border bg-card flex flex-col h-full"
            data-testid="task-detail-panel-full"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20 flex-shrink-0">
                <Badge className={statusConfig[task.status]?.color}>
                    {statusConfig[task.status]?.emoji} {statusConfig[task.status]?.label}
                </Badge>
                <div className="flex items-center gap-1">
                    {!isEditing ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsEditing(true)} data-testid="edit-task-btn">
                            <Edit3 className="w-4 h-4" />
                        </Button>
                    ) : (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-green-500" onClick={handleSave} data-testid="save-task-btn">
                            <Save className="w-4 h-4" />
                        </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete?.(task.id)} data-testid="delete-task-btn">
                        <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCloseClick} data-testid="close-panel-btn">
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                    {/* Title */}
                    {isEditing ? (
                        <Input
                            value={editData.title}
                            onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                            className="text-xl font-semibold"
                            data-testid="edit-title-input"
                        />
                    ) : (
                        <div>
                            <h2 className="text-xl font-semibold leading-tight">{task.title}</h2>
                            {task.is_recurring && (
                                <Badge variant="outline" className="gap-1 text-xs mt-2">
                                    <RefreshCw className="w-3 h-3" />
                                    Wiederkehrend ({task.recurrence_pattern})
                                </Badge>
                            )}
                            {task.reminders?.length > 0 && (
                                <Badge variant="outline" className="gap-1 text-xs mt-2 ml-1" data-testid="task-reminders-badge">
                                    <Bell className="w-3 h-3" />
                                    Erinnerung {task.reminders.map(formatReminder).join(', ')} vorher
                                </Badge>
                            )}
                        </div>
                    )}

                    {/* ===== TIME TRACKING SECTION - More prominent when inactive ===== */}
                    <div className={`rounded-lg p-4 transition-all ${
                        isTrackingThisTask 
                            ? 'bg-primary/10 border-2 border-primary/40 shadow-lg shadow-primary/10' 
                            : 'bg-gradient-to-r from-cyan-500/10 via-primary/10 to-violet-500/10 border border-primary/20 hover:border-primary/40'
                    }`}>
                        <button 
                            onClick={() => toggleSection('time')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <span className={`text-sm font-medium flex items-center gap-2 ${!isTrackingThisTask ? 'text-primary' : ''}`}>
                                <Clock className={`w-4 h-4 ${!isTrackingThisTask ? 'text-primary' : ''}`} />
                                Zeiterfassung
                                {!isTrackingThisTask && !isTracking && (
                                    <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                                        Bereit
                                    </Badge>
                                )}
                            </span>
                            {expandedSections.time ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        <AnimatePresence>
                            {expandedSections.time && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-3 space-y-3"
                                >
                                    {/* Active Timer Display */}
                                    {isTrackingThisTask && (
                                        <div className="bg-primary/10 rounded-lg p-3 border border-primary/30">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-muted-foreground">Aktive Zeit:</span>
                                                <span className="text-2xl font-mono font-bold text-primary">
                                                    {formattedTime || formatTimeDisplay(elapsedSeconds)}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Timer Controls */}
                                    {!isTrackingThisTask && !showTimeForm && (
                                        <Button
                                            onClick={() => onStartTracking(task.id, task.project_id)}
                                            className="w-full bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-600 hover:to-primary/90 text-white shadow-md"
                                            data-testid="start-tracking-btn"
                                        >
                                            <Play className="w-4 h-4 mr-2" />
                                            Zeit starten
                                        </Button>
                                    )}

                                    {isTrackingThisTask && !showTimeForm && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => setShowTimeForm(true)}
                                                className="flex-1 bg-red-500 hover:bg-red-600"
                                                data-testid="stop-tracking-btn"
                                            >
                                                <Square className="w-4 h-4 mr-2" />
                                                Stoppen & Speichern
                                            </Button>
                                            <Button
                                                onClick={handleCancelTime}
                                                variant="outline"
                                                className="text-muted-foreground"
                                                data-testid="cancel-tracking-btn"
                                            >
                                                Verwerfen
                                            </Button>
                                        </div>
                                    )}

                                    {/* Total Time */}
                                    {task.total_time_seconds > 0 && (
                                        <div className="flex items-center justify-between text-sm pt-2 border-t border-border/50">
                                            <span className="text-muted-foreground">Gesamt erfasst:</span>
                                            <span className="font-mono font-medium">
                                                {Math.floor(task.total_time_seconds / 3600)}h {Math.floor((task.total_time_seconds % 3600) / 60)}m
                                            </span>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Stop Time Modal - Same Design as TimePage */}
                    <Dialog open={showTimeForm} onOpenChange={setShowTimeForm}>
                        <DialogContent className="max-w-md p-0 overflow-hidden">
                            {/* Header with gradient */}
                            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-3 text-xl">
                                        <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                            <span className="text-2xl">⏱️</span>
                                        </div>
                                        <div>
                                            <p className="font-semibold">Zeit stoppen</p>
                                            <p className="text-3xl font-mono font-bold text-primary">
                                                {formattedTime || formatTimeDisplay(elapsedSeconds)}
                                            </p>
                                        </div>
                                    </DialogTitle>
                                </DialogHeader>
                            </div>
                            
                            <div className="p-6 pt-2 space-y-5">
                                {/* Quick Actions - iOS Style Cards */}
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => {
                                            setTimeStopForm(prev => ({ ...prev, activity_type: 'Pause', description: 'Pause' }));
                                            handleStopTime();
                                        }}
                                        className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all hover:scale-[1.02]"
                                    >
                                        <span className="text-3xl">☕</span>
                                        <span className="font-medium text-amber-700 dark:text-amber-400">Pause</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setTimeStopForm(prev => ({ ...prev, activity_type: 'Allgemein', customer_id: '' }));
                                            handleStopTime();
                                        }}
                                        className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20 transition-all hover:scale-[1.02]"
                                    >
                                        <span className="text-3xl">📋</span>
                                        <span className="font-medium text-slate-700 dark:text-slate-400">Allgemein</span>
                                    </button>
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-dashed" />
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">oder buchen auf</span>
                                    </div>
                                </div>

                                {/* Customer Dropdown with Logos */}
                                <div>
                                    <Label className="flex items-center gap-2 text-sm font-medium">
                                        <span>🏢</span> Kunde
                                    </Label>
                                    <Select 
                                        value={timeStopForm.customer_id || 'none'} 
                                        onValueChange={(v) => setTimeStopForm(prev => ({ ...prev, customer_id: v === 'none' ? '' : v }))}
                                    >
                                        <SelectTrigger className="mt-2 h-12 rounded-xl">
                                            <SelectValue placeholder="Kunde auswählen..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Kein Kunde</SelectItem>
                                            {customers?.map(c => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="w-7 h-7 rounded-lg">
                                                            {c.logo_url ? (
                                                                <AvatarImage src={c.logo_url} className="object-cover" />
                                                            ) : null}
                                                            <AvatarFallback 
                                                                style={{ backgroundColor: c.ci_colors?.[0] || '#009dde' }} 
                                                                className="text-xs text-white rounded-lg"
                                                            >
                                                                {c.name?.substring(0, 2).toUpperCase()}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <span className="font-medium">{c.name}</span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Activity Type */}
                                <div>
                                    <Label className="flex items-center gap-2 text-sm font-medium">
                                        <span>🎯</span> Aktivität
                                    </Label>
                                    <Select
                                        value={timeStopForm.activity_type}
                                        onValueChange={(v) => setTimeStopForm(prev => ({ ...prev, activity_type: v }))}
                                    >
                                        <SelectTrigger className="mt-2 h-12 rounded-xl">
                                            <SelectValue placeholder="Was hast du gemacht?" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {activityTypes?.map(a => (
                                                <SelectItem key={a.id} value={a.name}>
                                                    <div className="flex items-center gap-2">
                                                        <span>
                                                            {a.name === 'Grafik' && '🎨'}
                                                            {a.name === 'Videoschnitt' && '🎬'}
                                                            {a.name === 'Reporting' && '📊'}
                                                            {a.name === 'Meeting' && '👥'}
                                                            {!['Grafik', 'Videoschnitt', 'Reporting', 'Meeting'].includes(a.name) && '📌'}
                                                        </span>
                                                        {a.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Description */}
                                <div>
                                    <Label className="flex items-center gap-2 text-sm font-medium">
                                        <span>💬</span> Beschreibung (optional)
                                    </Label>
                                    <Input
                                        className="mt-2 h-12 rounded-xl"
                                        placeholder="Was wurde gemacht?"
                                        value={timeStopForm.description}
                                        onChange={(e) => setTimeStopForm(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1 h-12 rounded-xl"
                                        onClick={() => setShowTimeForm(false)}
                                    >
                                        Abbrechen
                                    </Button>
                                    <Button 
                                        className="flex-1 h-12 rounded-xl"
                                        onClick={handleStopTime}
                                        data-testid="save-time-btn"
                                    >
                                        Zeit speichern
                                    </Button>
                                </div>

                                {/* Cancel Timer Option */}
                                <button
                                    onClick={handleCancelTime}
                                    className="w-full text-center text-sm text-muted-foreground hover:text-destructive transition-colors"
                                >
                                    Timer ohne Speichern abbrechen
                                </button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* ===== DETAILS SECTION ===== */}
                    <div className="bg-muted/30 rounded-lg p-4">
                        <button 
                            onClick={() => toggleSection('details')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <span className="text-sm font-medium">Details</span>
                            {expandedSections.details ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        <AnimatePresence>
                            {expandedSections.details && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-3 space-y-4"
                                >
                                    {/* Status */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Status</Label>
                                        {isEditing ? (
                                            <Select
                                                value={editData.status}
                                                onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(statusConfig).map(([key, config]) => (
                                                        <SelectItem key={key} value={key}>
                                                            {config.emoji} {config.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Badge className={`mt-1 ${statusConfig[task.status]?.color}`}>
                                                {statusConfig[task.status]?.emoji} {statusConfig[task.status]?.label}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Flag className="w-3 h-3" /> Priorität
                                        </Label>
                                        {isEditing ? (
                                            <Select
                                                value={String(editData.priority)}
                                                onValueChange={(v) => setEditData(prev => ({ ...prev, priority: Number(v) }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="1">Hoch</SelectItem>
                                                    <SelectItem value="2">Mittel</SelectItem>
                                                    <SelectItem value="3">Niedrig</SelectItem>
                                                    <SelectItem value="0">Nicht dringend</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Badge className={`mt-1 ${priorityConfig[task.priority]?.bgLight} ${priorityConfig[task.priority]?.textColor}`}>
                                                {priorityConfig[task.priority]?.label}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Due Date */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Calendar className="w-3 h-3" /> Fälligkeitsdatum
                                        </Label>
                                        {isEditing ? (
                                            <div className="flex gap-2 mt-1">
                                                <Input
                                                    type="date"
                                                    value={editData.deadline}
                                                    onChange={(e) => setEditData(prev => ({ ...prev, deadline: e.target.value }))}
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="time"
                                                    value={editData.deadline_time}
                                                    onChange={(e) => setEditData(prev => ({ ...prev, deadline_time: e.target.value }))}
                                                    className="w-24"
                                                />
                                            </div>
                                        ) : task.deadline ? (
                                            <div className={`mt-1 flex items-center gap-2 ${isOverdue ? 'text-red-500' : ''}`}>
                                                {isOverdue && <AlertCircle className="w-4 h-4" />}
                                                <span className={`px-2 py-1 rounded text-sm ${isOverdue ? 'bg-red-500/10' : 'bg-muted'}`}>
                                                    {format(new Date(task.deadline), 'PPP', { locale: de })}
                                                    {task.deadline.includes('T') && (
                                                        <span className="ml-2">
                                                            {format(new Date(task.deadline), 'HH:mm')} Uhr
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1">Kein Datum</p>
                                        )}
                                    </div>

                                    {/* Project - hidden if disabled */}
                                    {projectsEnabled && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <FolderKanban className="w-3 h-3" /> Projekt
                                        </Label>
                                        {isEditing ? (
                                            <Select
                                                value={editData.project_id || 'none'}
                                                onValueChange={(v) => setEditData(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="Projekt auswählen..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Kein Projekt</SelectItem>
                                                    {projects?.map(p => (
                                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : project ? (
                                            <Badge 
                                                variant="outline" 
                                                className="mt-1"
                                                style={{ borderColor: project.primary_color, color: project.primary_color }}
                                            >
                                                {project.name}
                                            </Badge>
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1">Kein Projekt</p>
                                        )}
                                    </div>
                                    )}

                                    {/* Assigned Users - Dropdown instead of checkboxes */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Users className="w-3 h-3" /> Zugewiesen
                                        </Label>
                                        {isEditing ? (
                                            <Select
                                                value={editData.assigned_to?.[0] || 'none'}
                                                onValueChange={(v) => setEditData(prev => ({ 
                                                    ...prev, 
                                                    assigned_to: v === 'none' ? [] : [v]
                                                }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="Person zuweisen..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Niemand zugewiesen</SelectItem>
                                                    {users?.map(user => (
                                                        <SelectItem key={user.id} value={user.id}>
                                                            <div className="flex items-center gap-2">
                                                                <div 
                                                                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px]"
                                                                    style={{ backgroundColor: user.profile_color }}
                                                                >
                                                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                                                </div>
                                                                {user.first_name} {user.last_name}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : task.assigned_users?.length > 0 ? (
                                            <div className="mt-1 space-y-2">
                                                {task.assigned_users.map(user => (
                                                    <div key={user.id} className="flex items-center gap-2">
                                                        <Avatar className="w-6 h-6">
                                                            <AvatarFallback style={{ backgroundColor: user.profile_color }} className="text-white text-xs">
                                                                {user.first_name?.[0]}{user.last_name?.[0]}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            ({user.display_title || user.role})
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1">Niemand zugewiesen</p>
                                        )}
                                    </div>

                                    {/* Account Manager */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <User className="w-3 h-3" /> Account Manager
                                        </Label>
                                        {isEditing ? (
                                            <Select
                                                value={editData.account_manager_id || 'none'}
                                                onValueChange={(v) => setEditData(prev => ({ ...prev, account_manager_id: v === 'none' ? '' : v }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="Account Manager..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Kein Account Manager</SelectItem>
                                                    {accountManagers?.map(user => (
                                                        <SelectItem key={user.id} value={user.id}>
                                                            <div className="flex items-center gap-2">
                                                                <div 
                                                                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px]"
                                                                    style={{ backgroundColor: user.profile_color }}
                                                                >
                                                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                                                </div>
                                                                {user.first_name} {user.last_name}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : task.account_manager_id ? (
                                            (() => {
                                                const am = users?.find(u => u.id === task.account_manager_id);
                                                return am ? (
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Avatar className="w-6 h-6">
                                                            <AvatarFallback style={{ backgroundColor: am.profile_color }} className="text-white text-xs">
                                                                {am.first_name?.[0]}{am.last_name?.[0]}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <span className="text-sm">{am.first_name} {am.last_name}</span>
                                                    </div>
                                                ) : <p className="text-sm text-muted-foreground mt-1">Unbekannt</p>;
                                            })()
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1">Kein Account Manager</p>
                                        )}
                                    </div>

                                    {/* Estimated Hours / Workload */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> Zeitschätzung (Stunden)
                                        </Label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                step="0.5"
                                                min="0"
                                                value={editData.estimated_hours}
                                                onChange={(e) => setEditData(prev => ({ ...prev, estimated_hours: e.target.value }))}
                                                placeholder="z.B. 2.5"
                                                className="mt-1"
                                            />
                                        ) : task.estimated_hours ? (
                                            <p className="text-sm mt-1">{task.estimated_hours}h geschätzt</p>
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1">Keine Schätzung</p>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Beschreibung</Label>
                                        {isEditing ? (
                                            <Textarea
                                                value={editData.description}
                                                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                                                className="mt-1"
                                                rows={3}
                                                placeholder="Beschreibung hinzufügen..."
                                            />
                                        ) : task.description ? (
                                            <p className="text-sm mt-1 whitespace-pre-wrap bg-muted/50 p-2 rounded">{task.description}</p>
                                        ) : (
                                            <p className="text-sm text-muted-foreground mt-1 italic">Keine Beschreibung</p>
                                        )}
                                    </div>

                                    {/* Recurring Settings */}
                                    {isEditing && (
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                                <RefreshCw className="w-3 h-3" /> Wiederkehrend
                                            </Label>
                                            <Switch
                                                checked={editData.is_recurring}
                                                onCheckedChange={(v) => setEditData(prev => ({ ...prev, is_recurring: v }))}
                                            />
                                        </div>
                                    )}
                                    {isEditing && editData.is_recurring && (
                                        <Select
                                            value={editData.recurrence_pattern}
                                            onValueChange={(v) => setEditData(prev => ({ ...prev, recurrence_pattern: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Wiederholung..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="daily">Täglich</SelectItem>
                                                <SelectItem value="weekly">Wöchentlich</SelectItem>
                                                <SelectItem value="monthly">Monatlich</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}

                                    {/* Erinnerungen vor der Deadline (wie beim Kalender) */}
                                    {isEditing && (
                                        <div>
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1.5">
                                                <Bell className="w-3 h-3" /> Erinnerung vor der Deadline
                                            </Label>
                                            <TaskReminderPicker
                                                compact
                                                value={editData.reminders || []}
                                                onChange={(v) => setEditData(prev => ({ ...prev, reminders: v }))}
                                            />
                                            {!editData.deadline && (editData.reminders || []).length > 0 && (
                                                <p className="text-[11px] text-amber-500 mt-1">Ohne Deadline wird keine Erinnerung gesendet.</p>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ===== CHECKLIST SECTION (optional) ===== */}
                    <div className="bg-muted/30 rounded-lg p-4">
                        <button
                            onClick={() => toggleSection('checklist')}
                            className="flex items-center justify-between w-full text-left"
                            data-testid="toggle-checklist-section"
                        >
                            <span className="text-sm font-medium flex items-center gap-2">
                                <ListChecks className="w-4 h-4" />
                                Checkliste
                                {task.checklist?.length > 0 && (
                                    <span className="text-xs text-muted-foreground font-normal">
                                        ({task.checklist.filter(i => i.done).length}/{task.checklist.length})
                                    </span>
                                )}
                            </span>
                            {expandedSections.checklist ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        <AnimatePresence>
                            {expandedSections.checklist && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-3"
                                >
                                    <TaskChecklist task={task} onUpdate={onUpdate} />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ===== COMMENTS SECTION ===== */}
                    <div className="bg-muted/30 rounded-lg p-4">
                        <button 
                            onClick={() => toggleSection('comments')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <span className="text-sm font-medium flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                Kommentare ({task.comments?.length || 0})
                            </span>
                            {expandedSections.comments ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        <AnimatePresence>
                            {expandedSections.comments && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-3 space-y-3"
                                >
                                    {/* Add Comment */}
                                    <div className="flex gap-2">
                                        <Input
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            placeholder="Kommentar schreiben..."
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                            data-testid="comment-input"
                                        />
                                        <Button size="icon" onClick={handleAddComment} data-testid="add-comment-btn">
                                            <Send className="w-4 h-4" />
                                        </Button>
                                    </div>

                                    {/* Comments List */}
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                        {task.comments?.length > 0 ? (
                                            task.comments.slice().reverse().map(comment => (
                                                <div key={comment.id} className="bg-background rounded-lg p-3 group relative">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-sm">{comment.user_name}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: de })}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm">{comment.content}</p>
                                                    <button
                                                        onClick={() => onDeleteComment?.(task.id, comment.id)}
                                                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/10 rounded"
                                                    >
                                                        <Trash2 className="w-3 h-3 text-red-500" />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground text-center py-2">Keine Kommentare</p>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ===== FILES SECTION ===== */}
                    <div className="bg-muted/30 rounded-lg p-4">
                        <button 
                            onClick={() => toggleSection('files')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Paperclip className="w-4 h-4" />
                                Anhänge ({task.files?.length || 0})
                            </span>
                            {expandedSections.files ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        <AnimatePresence>
                            {expandedSections.files && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-3 space-y-3"
                                >
                                    {/* Upload Button */}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="upload-file-btn"
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        {uploading ? 'Lädt...' : 'Datei hochladen'}
                                    </Button>

                                    {/* Files List */}
                                    <div className="space-y-2">
                                        {task.files?.length > 0 ? (
                                            task.files.map((file, i) => (
                                                <div key={i} className="flex items-center justify-between bg-background rounded-lg p-2 group">
                                                    <a
                                                        href={file.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 text-sm hover:text-primary truncate flex-1"
                                                    >
                                                        {getFileIcon(file.name || file.filename)}
                                                        <span className="truncate">{file.name || file.filename}</span>
                                                    </a>
                                                    <button
                                                        onClick={() => onFileDelete?.(task.id, file.key || file.id)}
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/10 rounded"
                                                    >
                                                        <Trash2 className="w-3 h-3 text-red-500" />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground text-center py-2">Keine Anhänge</p>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Meta Info */}
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                        <p>Erstellt: {format(new Date(task.created_at), 'PPP HH:mm', { locale: de })}</p>
                        {task.updated_at && (
                            <p>Zuletzt bearbeitet: {format(new Date(task.updated_at), 'PPP HH:mm', { locale: de })}</p>
                        )}
                    </div>
                </div>
            </ScrollArea>
        </motion.div>
    );
};

export default TaskDetailPanelFull;
